import { Alert } from "@nimbus-ds/components/Alert";
import { Button } from "@nimbus-ds/components/Button";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import {
  ApiError,
  fetchInstallationConfig,
  saveInstallationConfig,
} from "./api";
import { AgentLifecycleControls } from "./AgentLifecycleControls";
import { FullPageState } from "./Dashboard";
import type { CommunicationLevel } from "./types";

const AUTONOMY_LABELS: Record<string, string> = {
  default: "Contrato original",
  propose_only: "Solo propone",
  propose_and_approve: "Propone y aprobás",
  autonomous: "Autónomo",
};

// Auto-save (2026-06-22) — a stable serialization of every savable field. The
// auto-save watcher compares the live key against a baseline captured at
// hydration (and after each successful save); a difference means the merchant
// changed something, so we debounce a save. Connector keys are sorted so the
// key is insertion-order independent.
type SavableConfig = {
  connectorSelections: Record<string, string>;
  autonomyMode: string;
  approvalPosture: string;
  autoRollbackOptOut: boolean;
  communicationLevel: string;
  scheduleInterval: number;
  scheduleHour: number;
  scheduleWeekday: number | null;
  schedulePaused: boolean;
};

function buildConfigKey(v: SavableConfig): string {
  return JSON.stringify({
    c: Object.keys(v.connectorSelections)
      .sort()
      .map((k) => [k, v.connectorSelections[k]]),
    a: v.autonomyMode,
    p: v.approvalPosture,
    r: v.autoRollbackOptOut,
    l: v.communicationLevel,
    si: v.scheduleInterval,
    sh: v.scheduleHour,
    sw: v.scheduleWeekday,
    sp: v.schedulePaused,
  });
}

// Plain-Spanish, one-line "qué lee" for each capability, mirrored from the
// backend `catalog.CAPABILITY_LABEL_ES`. The wire only sends the raw
// `capability_id` (e.g. "commerce.orders.read"), which is internal jargon a
// merchant must never see, so we resolve it here to a humane description.
// New capabilities should land in both maps together.
const CAPABILITY_READS_ES: Record<string, string> = {
  "commerce.store.read": "Configuración general, monedas y datos de la tienda.",
  "commerce.orders.read": "Pedidos, montos y estados de los últimos días.",
  "commerce.products.read": "Productos, precios y variantes publicados.",
  "commerce.inventory.read": "Existencias y movimientos de inventario.",
  "reviews.read": "Opiniones y puntajes que dejan tus compradores.",
  "ads.meta.performance.read": "Inversión, alcance y resultados de tus avisos.",
  "ads.meta.actions.write_limited": "Ajustes acotados sobre tus campañas.",
  "analytics.web.read": "Visitas, fuentes de tráfico y conversiones del sitio.",
};

function capabilityReadsCopy(capabilityId: string): string {
  return (
    CAPABILITY_READS_ES[capabilityId] ??
    "Datos de esta fuente para sus análisis."
  );
}

// L90 — Cadencia section helpers

const INTERVAL_OPTIONS: { value: number; label: string }[] = [
  { value: 360,   label: "6 horas" },
  { value: 720,   label: "12 horas" },
  { value: 1440,  label: "1 día" },
  { value: 2880,  label: "2 días" },
  { value: 4320,  label: "3 días" },
  { value: 10080, label: "1 semana" },
  { value: 20160, label: "2 semanas" },
  { value: 43200, label: "1 mes" },
];

const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => ({
  value: i,
  label: `${String(i).padStart(2, "0")}:00`,
}));

const WEEKDAY_OPTIONS = [
  { value: 0, label: "Lunes" },
  { value: 1, label: "Martes" },
  { value: 2, label: "Miércoles" },
  { value: 3, label: "Jueves" },
  { value: 4, label: "Viernes" },
  { value: 5, label: "Sábado" },
  { value: 6, label: "Domingo" },
];

function filteredIntervalOptions(
  band: { min_minutes: number; max_minutes: number } | null,
): { value: number; label: string }[] {
  if (!band) return INTERVAL_OPTIONS;
  return INTERVAL_OPTIONS.filter(
    (o) => o.value >= band.min_minutes && o.value <= band.max_minutes,
  );
}

export function AgentConfigure() {
  const { installationId = "" } = useParams<{ installationId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const configQuery = useQuery({
    queryKey: ["agents", "installation-config", installationId],
    queryFn: () => fetchInstallationConfig(installationId),
    enabled: Boolean(installationId),
    retry: false,
  });

  const [autonomyMode, setAutonomyMode] = useState("");
  const [approvalPosture, setApprovalPosture] = useState<
    "standard" | "conservative"
  >("standard");
  const [autoRollbackOptOut, setAutoRollbackOptOut] = useState(false);
  const [communicationLevel, setCommunicationLevel] =
    useState<CommunicationLevel>("expert");
  const [connectorSelections, setConnectorSelections] = useState<
    Record<string, string>
  >({});

  // L90 — cadence picker state
  const [scheduleInterval, setScheduleInterval] = useState(0);
  const [scheduleHour, setScheduleHour] = useState(8);
  const [scheduleWeekday, setScheduleWeekday] = useState<number | null>(null);
  const [schedulePaused, setSchedulePaused] = useState(false);
  const [scheduleError, setScheduleError] = useState("");

  // Baseline config key captured at hydration + after each successful save.
  // The auto-save watcher saves only when the live key drifts from this.
  const savedConfigKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const data = configQuery.data;
    if (!data) return;
    const connectorSelectionsFromServer = Object.fromEntries(
      data.connector_slots.map((slot) => [
        slot.capability_id,
        slot.current_account_id || slot.available_accounts[0]?.id || "",
      ]),
    );
    const s = data.installation.schedule;
    const scheduleIntervalFromServer = s ? s.interval_minutes : 0;
    const scheduleHourFromServer = s ? s.preferred_local_hour : 8;
    const scheduleWeekdayFromServer = s ? s.preferred_local_weekday ?? null : null;
    const schedulePausedFromServer = s ? s.paused : false;

    setAutonomyMode(data.contract.autonomy_mode);
    setApprovalPosture(data.contract.approval_posture);
    setAutoRollbackOptOut(data.contract.auto_rollback_opt_out);
    setCommunicationLevel(data.communication_level);
    setConnectorSelections(connectorSelectionsFromServer);
    if (s) {
      setScheduleInterval(s.interval_minutes);
      setScheduleHour(s.preferred_local_hour);
      setScheduleWeekday(s.preferred_local_weekday ?? null);
      setSchedulePaused(s.paused);
    }

    // Capture the baseline from the SAME server values the state was just set
    // to, so the live key matches it after hydration and no spurious save
    // fires. (Built here, not from state, because setState is async.)
    savedConfigKeyRef.current = buildConfigKey({
      connectorSelections: connectorSelectionsFromServer,
      autonomyMode: data.contract.autonomy_mode,
      approvalPosture: data.contract.approval_posture,
      autoRollbackOptOut: data.contract.auto_rollback_opt_out,
      communicationLevel: data.communication_level,
      scheduleInterval: scheduleIntervalFromServer,
      scheduleHour: scheduleHourFromServer,
      scheduleWeekday: scheduleWeekdayFromServer,
      schedulePaused: schedulePausedFromServer,
    });
  }, [configQuery.data]);

  const saveMutation = useMutation({
    mutationFn: () =>
      saveInstallationConfig(installationId, {
        connector_account_ids: connectorSelections,
        autonomy_mode: autonomyMode,
        approval_posture: approvalPosture,
        auto_rollback_opt_out: autoRollbackOptOut,
        communication_level: communicationLevel,
        // L90 — include the schedule only when there is a real one to send.
        // An installation with no seeded schedule leaves scheduleInterval=0;
        // the backend rejects interval<=0 (400) BEFORE applying the rest of the
        // contract, which would silently drop the other changes. Omitting the
        // key entirely tells the backend "no schedule change" and lets the
        // connector/autonomy/communication edits persist. (#5 DA fix.)
        ...(scheduleInterval > 0
          ? {
              schedule: {
                kind: "interval" as const,
                interval_minutes: scheduleInterval,
                preferred_local_hour: scheduleHour,
                preferred_local_weekday: scheduleWeekday,
                jitter_minutes: 15,
                paused: schedulePaused,
                revision:
                  configQuery.data?.installation?.schedule?.revision ?? 0,
              },
            }
          : {}),
      }),
    onSuccess: (payload) => {
      // Auto-save (2026-06-22): persist + refresh, but stay on the page (no
      // navigate-away). The populate effect re-runs from the echoed payload and
      // resets the baseline key, so the watcher settles without re-firing.
      queryClient.setQueryData(
        ["agents", "installation-config", installationId],
        payload,
      );
      queryClient.invalidateQueries({ queryKey: ["bootstrap"] });
    },
    onError: (err) => {
      if (err instanceof ApiError && err.message.includes("banda")) {
        setScheduleError("La cadencia disponible cambió; recargá la página.");
      } else if (
        err instanceof ApiError &&
        /revision|stale/i.test(`${err.code ?? ""} ${err.message}`)
      ) {
        // A newer save landed first (revision race) — tell the merchant to
        // refresh rather than showing the raw backend error. (#5 DA fix.)
        setScheduleError(
          "Se guardó una versión más nueva en otra pestaña; recargá la página.",
        );
      }
    },
  });

  const hasMissingSelection = useMemo(
    () =>
      (configQuery.data?.connector_slots ?? []).some(
        (slot) => !connectorSelections[slot.capability_id],
      ),
    [configQuery.data?.connector_slots, connectorSelections],
  );

  // #5 (2026-06-22) — auto-save: a stable key of the live config; when it
  // drifts from the hydration baseline (and selections are complete), debounce
  // a save. No "Guardar" button — changes persist on their own.
  const liveConfigKey = buildConfigKey({
    connectorSelections,
    autonomyMode,
    approvalPosture,
    autoRollbackOptOut,
    communicationLevel,
    scheduleInterval,
    scheduleHour,
    scheduleWeekday,
    schedulePaused,
  });
  // True when the live config differs from the last-saved baseline (i.e. there
  // are changes that haven't been persisted yet — the debounce window).
  const hasUnsavedDrift =
    savedConfigKeyRef.current !== null &&
    liveConfigKey !== savedConfigKeyRef.current;
  const saveMutate = saveMutation.mutate;
  const savePending = saveMutation.isPending;
  useEffect(() => {
    // Not hydrated yet, or no real change vs. the last saved state → nothing
    // to do. (`savedConfigKeyRef` is set by the populate effect.)
    if (savedConfigKeyRef.current === null) return;
    if (liveConfigKey === savedConfigKeyRef.current) return;
    // Never auto-save an incomplete connector selection — it would 422.
    if (hasMissingSelection) return;
    // Serialize saves: don't fire a second one while the first is in flight.
    // The backend bumps the schedule revision on every save, so overlapping
    // saves would make the second post a stale revision (409). When the
    // in-flight save settles, isPending flips and this effect re-runs; if the
    // config still drifts from the (now-refreshed) baseline, it saves then.
    if (savePending) return;
    const timer = setTimeout(() => {
      savedConfigKeyRef.current = liveConfigKey;
      saveMutate();
    }, 700);
    return () => clearTimeout(timer);
  }, [liveConfigKey, hasMissingSelection, saveMutate, savePending]);

  if (configQuery.isLoading) {
    return (
      <FullPageState
        title="Leyendo configuración"
        body="Buscando contrato y fuentes del agente."
      />
    );
  }

  if (configQuery.isError || !configQuery.data) {
    const message =
      configQuery.error instanceof Error
        ? configQuery.error.message
        : "Volvé al tablero e intentá de nuevo.";
    return (
      <FullPageState title="No pudimos abrir la configuración" body={message} />
    );
  }

  const data = configQuery.data;
  const agent = data.installation;

  // v1 visible agents are all `analyst` — they observe and recommend, never
  // execute actions. For them `allowed_autonomy_modes` is just `["default"]`,
  // so the Autonomía / Aprobaciones / Reversiones automáticas controls govern
  // behaviours that can never happen (a single dead radio option + two action
  // policies for an agent that takes no actions). Render those three sections
  // only for actor agents (future), where they're real. The analyst contract
  // surface stays focused on what it actually controls: explanation level,
  // cadence, and connected sources.
  const isActorAgent = data.contract.allowed_autonomy_modes.some(
    (mode) => mode !== "default",
  );

  // Compact "En resumen" rail summary — derived purely from current state, no
  // new fetch or save-payload change.
  const cadenceSummary = schedulePaused
    ? "Pausada"
    : data.installation.slug === "manager"
      ? "Semanal"
      : (INTERVAL_OPTIONS.find((o) => o.value === scheduleInterval)?.label ??
        "Según el intervalo");

  // L73 P2: AgentDetailLayout owns identity now; this surface only renders
  // configure body. The Configuración tab also absorbs lifecycle controls
  // (pause / reactivate / uninstall) — the L58 typed-name confirm modal is
  // included verbatim via <AgentLifecycleControls/>.
  return (
    <div className="agentConfigureRoot">
      <section className="configureLayout">
        <div className="configureLayout__main">
        <article className="hireSection hireSection--hero">
          <h2 className="hireSection__title">Contrato activo</h2>
          <p>
            Lo que cambies acá empieza a regir en la próxima corrida y se guarda
            solo.
          </p>
          {!isActorAgent ? (
            <p className="subtle" data-testid="analyst-contract-note">
              Este agente solo observa y recomienda: no toca nada de tu tienda.
              Por eso lo que configurás es cómo te explica, cada cuánto corre y
              qué fuentes lee. No hay aprobaciones ni reversiones que administrar.
            </p>
          ) : null}
          {/* #4 (2026-06-22) — status + manage CTAs live at the bottom of the
              contract card (the agent is already introduced in the hero, so no
              persona pitch here). */}
          <AgentLifecycleControls
            agent={agent}
            onAfterUninstall={() => navigate("/")}
          />
        </article>

        {isActorAgent ? (
          <article className="hireSection">
            <h2>Autonomía</h2>
            <div className="optionGrid" role="radiogroup" aria-label="Autonomía">
              {data.contract.allowed_autonomy_modes.map((mode) => (
                <label
                  key={mode}
                  className={`configOption${autonomyMode === mode ? " selected" : ""}`}
                >
                  <input
                    type="radio"
                    name="autonomy_mode"
                    value={mode}
                    checked={autonomyMode === mode}
                    onChange={() => setAutonomyMode(mode)}
                  />
                  <span>
                    <strong>{AUTONOMY_LABELS[mode] ?? mode}</strong>
                    <small>{autonomyDescription(mode)}</small>
                  </span>
                </label>
              ))}
            </div>
          </article>
        ) : null}

        {isActorAgent ? (
        <article className="hireSection">
          <h2>Aprobaciones</h2>
          <div
            className="optionGrid"
            role="radiogroup"
            aria-label="Aprobaciones"
          >
            <label
              className={`configOption${approvalPosture === "standard" ? " selected" : ""}`}
            >
              <input
                type="radio"
                name="approval_posture"
                value="standard"
                checked={approvalPosture === "standard"}
                onChange={() => setApprovalPosture("standard")}
              />
              <span>
                <strong>Estándar</strong>
                <small>
                  Respeta los límites del agente y pide aprobación solo cuando
                  la política lo exige.
                </small>
              </span>
            </label>
            <label
              className={`configOption${approvalPosture === "conservative" ? " selected" : ""}`}
            >
              <input
                type="radio"
                name="approval_posture"
                value="conservative"
                checked={approvalPosture === "conservative"}
                onChange={() => setApprovalPosture("conservative")}
              />
              <span>
                <strong>Conservador</strong>
                <small>Toda acción futura requiere tu aprobación.</small>
              </span>
            </label>
          </div>
        </article>
        ) : null}

        {isActorAgent ? (
        <article className="hireSection">
          <h2>Reversiones automáticas</h2>
          <p className="subtle">
            Si un cambio del agente empeora el resultado, el sistema puede
            proponerte revertirlo (vos seguís decidiendo). Pausarlas no toca el
            deshacer manual.
          </p>
          <div
            className="optionGrid"
            role="radiogroup"
            aria-label="Reversiones automáticas"
          >
            <label
              className={`configOption${!autoRollbackOptOut ? " selected" : ""}`}
            >
              <input
                type="radio"
                name="auto_rollback_opt_out"
                value="active"
                checked={!autoRollbackOptOut}
                onChange={() => setAutoRollbackOptOut(false)}
              />
              <span>
                <strong>Activadas</strong>
                <small>
                  Si una decisión empeora el resultado, vas a ver una propuesta
                  de revertirla en tu cola de aprobaciones.
                </small>
              </span>
            </label>
            <label
              className={`configOption${autoRollbackOptOut ? " selected" : ""}`}
            >
              <input
                type="radio"
                name="auto_rollback_opt_out"
                value="paused"
                checked={autoRollbackOptOut}
                onChange={() => setAutoRollbackOptOut(true)}
              />
              <span>
                <strong>Pausadas</strong>
                <small>
                  El sistema no va a proponer reversiones. Vos seguís pudiendo
                  deshacer manualmente cualquier acción.
                </small>
              </span>
            </label>
          </div>
        </article>
        ) : null}

        <article className="hireSection">
          <h2>Nivel de explicación</h2>
          <p className="subtle">
            Cómo te explica este agente por defecto. Podés cambiarlo en cada
            tarjeta con el botón Experto ⇄ Simple, y el control “Nivel: Según el
            agente” del tablero respeta lo que elijas acá.
          </p>
          <div
            className="optionGrid"
            role="radiogroup"
            aria-label="Nivel de explicación"
          >
            <label
              className={`configOption${communicationLevel === "expert" ? " selected" : ""}`}
            >
              <input
                type="radio"
                name="communication_level"
                value="expert"
                checked={communicationLevel === "expert"}
                onChange={() => setCommunicationLevel("expert")}
              />
              <span>
                <strong>Experto</strong>
                <small>
                  Con todos los datos, porcentajes, citas a tus fuentes y
                  detalle técnico.
                </small>
              </span>
            </label>
            <label
              className={`configOption${communicationLevel === "novice" ? " selected" : ""}`}
            >
              <input
                type="radio"
                name="communication_level"
                value="novice"
                checked={communicationLevel === "novice"}
                onChange={() => setCommunicationLevel("novice")}
              />
              <span>
                <strong>Simple</strong>
                <small>
                  En palabras fáciles, sin tecnicismos, pensado para arrancar
                  sin saber de métricas.
                </small>
              </span>
            </label>
          </div>
        </article>

        {/* L90 — Cadencia section (schedule picker) */}
        <CadenciaSection
          slug={data.installation.slug}
          band={data.schedule_band}
          interval={scheduleInterval}
          hour={scheduleHour}
          weekday={scheduleWeekday}
          paused={schedulePaused}
          nextRunAt={data.installation.next_run_at}
          onIntervalChange={setScheduleInterval}
          onHourChange={setScheduleHour}
          onWeekdayChange={setScheduleWeekday}
          onPausedChange={setSchedulePaused}
        />
        {scheduleError ? (
          <Alert appearance="warning" title="La cadencia cambió">
            {scheduleError}
          </Alert>
        ) : null}

        <article className="hireSection">
          <h2>Fuentes conectadas</h2>
          <p className="subtle">
            De cada fuente que tenés conectada elegís qué cuenta usa este agente.
            Solo lee estos datos: nunca escribe ni los comparte.
          </p>
          {data.connector_slots.length === 0 ? (
            <p className="subtle" data-testid="connectors-empty">
              Este agente no necesita ninguna cuenta extra: trabaja con los datos
              de tu tienda que ya están conectados.
            </p>
          ) : (
            <div className="connectorList">
              {data.connector_slots.map((slot) => (
                <div
                  key={slot.capability_id}
                  className="connectorRow configureConnector"
                >
                  <div className="connectorRow__main">
                    <div>
                      <strong>{slot.display_name_es}</strong>
                      <p className="subtle">
                        {capabilityReadsCopy(slot.capability_id)}
                      </p>
                    </div>
                  </div>
                  <div className="accountPicker">
                    <div
                      className="accountPickerList"
                      role="radiogroup"
                      aria-label={`Cuenta para ${slot.display_name_es}`}
                    >
                      {slot.available_accounts.map((account) => (
                        <label
                          key={account.id}
                          className={`accountOption${connectorSelections[slot.capability_id] === account.id ? " selected" : ""}`}
                        >
                          <input
                            type="radio"
                            name={`account_${slot.capability_id}`}
                            value={account.id}
                            checked={
                              connectorSelections[slot.capability_id] ===
                              account.id
                            }
                            onChange={() =>
                              setConnectorSelections((current) => ({
                                ...current,
                                [slot.capability_id]: account.id,
                              }))
                            }
                          />
                          <span>
                            <strong>
                              {account.display_name ||
                                account.external_account_id ||
                                "Cuenta conectada"}
                            </strong>
                            {account.external_account_id ? (
                              <small>{account.external_account_id}</small>
                            ) : null}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </article>

        {saveMutation.isError ? (
          <Alert appearance="danger" title="No pudimos guardar">
            {saveMutation.error instanceof ApiError
              ? saveMutation.error.message
              : "Revisá la configuración y volvé a intentar."}
          </Alert>
        ) : null}
        </div>

        <aside className="configureLayout__rail">
          <article className="hireSection configureSummary">
            <h2 className="sectionTitle">En resumen</h2>
            <ul className="configureSummary__list">
              <li className="configureSummary__row">
                <span className="configureSummary__label">Explicación</span>
                <span className="configureSummary__value">
                  {communicationLevel === "novice" ? "Simple" : "Experto"}
                </span>
              </li>
              <li className="configureSummary__row">
                <span className="configureSummary__label">Cadencia</span>
                <span className="configureSummary__value">{cadenceSummary}</span>
              </li>
              <li className="configureSummary__row">
                <span className="configureSummary__label">Fuentes conectadas</span>
                <span className="configureSummary__value">
                  {data.connector_slots.length}
                </span>
              </li>
            </ul>
          </article>

          {/* #5 (2026-06-22) — no "Guardar configuración" button: every change
              auto-saves. This is the live status of that auto-save. */}
          <div
            className="configureAutosave"
            role="status"
            aria-live="polite"
            data-testid="configure-autosave-status"
          >
            {hasMissingSelection ? (
              <span className="configureAutosave__state configureAutosave__state--warning">
                Elegí una cuenta para cada fuente y se guarda solo.
              </span>
            ) : saveMutation.isPending ? (
              <span className="configureAutosave__state configureAutosave__state--saving">
                <span className="configureAutosave__dot" aria-hidden="true" />
                Guardando…
              </span>
            ) : hasUnsavedDrift ? (
              // Within the debounce window there are real unsaved changes — say
              // so honestly instead of a stale "guardado". (#5 DA minor.)
              <span className="configureAutosave__state configureAutosave__state--saving">
                <span className="configureAutosave__dot" aria-hidden="true" />
                Cambios sin guardar…
              </span>
            ) : saveMutation.isSuccess ? (
              <span className="configureAutosave__state configureAutosave__state--saved">
                ✓ Cambios guardados
              </span>
            ) : (
              <span className="configureAutosave__state">
                Los cambios se guardan solos.
              </span>
            )}
            <p className="configureLayout__railHint">
              Lo que cambies rige en la próxima corrida.
            </p>
          </div>
        </aside>
      </section>
    </div>
  );
}

function autonomyDescription(mode: string): string {
  if (mode === "propose_only")
    return "El agente prepara propuestas, pero no las deja listas para ejecutar.";
  if (mode === "propose_and_approve")
    return "El agente propone acciones y espera tu aprobación.";
  if (mode === "autonomous")
    return "Reservado para agentes aprobados fuera de v1.";
  return "Mantiene la autonomía definida en el contrato original.";
}

// ---------------------------------------------------------------------------
// L90 — CadenciaSection component + helpers
// ---------------------------------------------------------------------------

function CadenciaSection({
  slug,
  band,
  interval,
  hour,
  weekday,
  paused,
  nextRunAt,
  onIntervalChange,
  onHourChange,
  onWeekdayChange,
  onPausedChange,
}: {
  slug: string;
  band: { min_minutes: number; max_minutes: number; default_minutes: number } | null;
  interval: number;
  hour: number;
  weekday: number | null;
  paused: boolean;
  nextRunAt: string | null | undefined;
  onIntervalChange: (v: number) => void;
  onHourChange: (v: number) => void;
  onWeekdayChange: (v: number | null) => void;
  onPausedChange: (v: boolean) => void;
}) {
  const isManager = slug === "manager";
  const options = filteredIntervalOptions(band);
  const showWeekday = interval >= 7 * 24 * 60;

  const nextRunLabel = nextRunAt ? formatNextRunChipLabel(nextRunAt) : null;

  return (
    <article className="hireSection" data-testid="cadencia-section">
      <h2>{isManager ? "Cadencia (semanal fija)" : "Cadencia"}</h2>
      {isManager ? (
        <p className="subtle">
          El resumen ejecutivo es semanal por diseño: abarca toda la semana de
          tu equipo de agentes, así que su cadencia no se cambia.
        </p>
      ) : null}

      {!isManager ? (
        <div className="cadenciaField">
          <label htmlFor="cadencia-interval" className="fieldLabel">Intervalo</label>
          <select
            id="cadencia-interval"
            className="cadenciaSelect"
            value={interval}
            disabled={paused}
            onChange={(e) => onIntervalChange(Number(e.target.value))}
            aria-label="Intervalo de corrida"
          >
            {options.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          {band ? (
            <p className="subtle cadenciaHint">
              Podés elegir desde {bandLabel(band.min_minutes)} hasta{" "}
              {bandLabel(band.max_minutes)}.
            </p>
          ) : null}
          {nextRunLabel ? (
            <p className="subtle cadenciaNextRun">
              Próxima corrida: {nextRunLabel}
            </p>
          ) : null}
        </div>
      ) : null}

      <div className="cadenciaField">
        <label htmlFor="cadencia-hour" className="fieldLabel">Hora preferida (horario local)</label>
        <select
          id="cadencia-hour"
          className="cadenciaSelect"
          value={hour}
          disabled={paused}
          onChange={(e) => onHourChange(Number(e.target.value))}
          aria-label="Hora preferida"
        >
          {HOUR_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {showWeekday || isManager ? (
        <div className="cadenciaField">
          <label htmlFor="cadencia-weekday" className="fieldLabel">Día preferido</label>
          <select
            id="cadencia-weekday"
            className="cadenciaSelect"
            value={weekday ?? 0}
            disabled={paused}
            onChange={(e) => onWeekdayChange(Number(e.target.value))}
            aria-label="Día preferido de la semana"
          >
            {WEEKDAY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <p className="subtle cadenciaHint">
            {isManager
              ? "El día en que querés recibir el resumen de la semana."
              : "Aplica cuando corre cada semana o con menos frecuencia."}
          </p>
        </div>
      ) : null}

      <div className="cadenciaField">
        <fieldset className="cadenciaToggle">
          <legend className="fieldLabel">Estado de la cadencia</legend>
          <div className="optionGrid" role="radiogroup" aria-label="Estado de la cadencia">
            <label className={`configOption${!paused ? " selected" : ""}`}>
              <input
                type="radio"
                name="schedule_paused"
                value="active"
                checked={!paused}
                onChange={() => onPausedChange(false)}
                aria-label="Cadencia activa"
              />
              <span>
                <strong>Activa</strong>
                <small>El agente va a correr según el intervalo configurado.</small>
              </span>
            </label>
            <label className={`configOption${paused ? " selected" : ""}`}>
              <input
                type="radio"
                name="schedule_paused"
                value="paused"
                checked={paused}
                onChange={() => onPausedChange(true)}
                aria-label="Cadencia pausada"
              />
              <span>
                <strong>Pausada</strong>
                <small>El agente no corre automáticamente. Podés correrlo manualmente.</small>
              </span>
            </label>
          </div>
        </fieldset>
      </div>
    </article>
  );
}

function formatNextRunChipLabel(iso: string): string | null {
  const target = new Date(iso);
  if (Number.isNaN(target.getTime())) return null;
  const deltaMs = target.getTime() - Date.now();
  if (deltaMs <= 0) return "ahora";
  const minutes = Math.round(deltaMs / 60_000);
  if (minutes < 60) return `en ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `en ${hours} h`;
  const days = Math.round(hours / 24);
  return `en ${days} d`;
}

function bandLabel(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  if (minutes < 24 * 60) return `${minutes / 60} h`;
  if (minutes === 7 * 24 * 60) return "1 semana";
  if (minutes === 30 * 24 * 60) return "1 mes";
  return `${minutes / (24 * 60)} d`;
}
